import {animate, animation, keyframes, query, state, style, transition, trigger, useAnimation} from "@angular/animations";

/*
 * Standard animasjoner for bruk i `animations` attributtet i @Component.
 *
 * Eksempel:
 * @Component({
 *    ....
 *    animations: [
 *        trigger('fadeIn', [
 *            transition(':enter', [useAnimation(animerOpacity0Til1, {params: {varighet: '1s'}})])
 *        ]),
 *    ],
 *    ....
 * })
 */

/** standard farge brukt ved blink $advarsel-100-lys */
const standardBlinkFarge = '#ffeab1'

/** animerer opacity fra 'start' til 'slutt' over en periode på 'varighet'*/
export const animerOpacity = animation([
    style({opacity: '{{start}}'}),
    animate('{{varighet}}', style({opacity: '{{slutt}}'})),
]);

/** fade inn element når dette oppstår i dom.
 * bruk: @Component({...., animations: [fadeInnAnimasjon()], ....})
 * og i html <element @fadeInn> */
export function fadeInnAnimasjon(varighet = '1s') {
    return trigger('fadeInn', [
        transition(':enter', [
            useAnimation(animerOpacity, {params: {varighet: varighet, start: 0, slutt: 1}})
        ])
    ]);
}

/** fade ut element når dette fjernes fra dom.
 * bruk: @Component({...., animations: [fadeUtAnimasjon()], ....})
 * og i html <element @fadeUt> */
export function fadeUtAnimasjon(varighet = '1s') {
    return trigger('fadeUt', [
        transition(':leave', [
            useAnimation(animerOpacity, {params: {varighet: varighet, start: 1, slutt: 0}})
        ])
    ]);
}

/** fade inn/ut element legges til/fjernes fra dom, animerer opacity.
 * bruk: @Component({...., animations: [fadeInnUtAnimasjon()], ....})
 * og i html <element @fadeInnUt> */
export function fadeInnUtAnimasjon(varighet = '1s') {
    return trigger('fadeInnUt', [
        transition(':enter', [useAnimation(animerOpacity, {params: {varighet: varighet, start: 0, slutt: 1}})]),
        transition('* => inn', [useAnimation(animerOpacity, {params: {varighet: varighet, start: 0, slutt: 1}})]),
        transition(':leave', [useAnimation(animerOpacity, {params: {varighet: varighet, start: 1, slutt: 0}})]),
        transition('* => ut', [useAnimation(animerOpacity, {params: {varighet: varighet, start: 1, slutt: 0}})])
    ]);
}


/** blinker ved å endre bakgrunnsfarge mellom initiell og angitt farge n (2 er standard) ganger */
export function animerBlink(innstillinger: Partial<{ varighet: string; initialFargefarge: string; blinkFarge: string; antallBlink: number }> = {}) {
    const {varighet, initialFargefarge, blinkFarge, antallBlink} = {varighet: '1s', initialFargefarge: 'initial', blinkFarge: standardBlinkFarge, antallBlink: 2, ...innstillinger}
    const offsetInkrement = 1 / (antallBlink*2);
    const frames = new Array(antallBlink).fill(null).flatMap((_, repetisjon) => {
        const offset = ((repetisjon*2)+1) * offsetInkrement ;
        return [
            style({background: blinkFarge, offset: offset}),
            style({background: initialFargefarge, offset: offset+offsetInkrement})
        ]
    });
    return animate(varighet, keyframes(frames));
}

/** blinker ved å endre bakgrunnsfarge mellom initiell og angitt farge en gang og fade sakte tilbake til initiell */
export function animerBlinkEnGangFadeUt(innstillinger: Partial<{ varighet: string; initialFargefarge: string; blinkFarge: string; }> = {}) {
    const {varighet, initialFargefarge, blinkFarge} = {varighet: '5s', initialFargefarge: 'initial', blinkFarge: standardBlinkFarge, ...innstillinger}
    return animate(varighet,
      keyframes([
          style({background: blinkFarge, offset: 0.05}),
          style({background: initialFargefarge, offset: 1}),
      ]));
}

/** blinker ved å endre bakgrunnsfarge mellom initiell og angitt farge to ganger, samt legge på en avrundet ramme  */
export function animerBlinkMedRamme(innstillinger: Partial<{ varighet: string; initialBakgrunnsfarge: string; bakgrunnsfarge: string; initialRammefarge: string; rammefarge: string}> = {}) {
    const param = {varighet: '1s', initialBakgrunnsfarge: 'initial', bakgrunnsfarge: standardBlinkFarge,initialRammefarge: 'initial', rammefarge:standardBlinkFarge, ...innstillinger}
    return animate(param.varighet,
      keyframes([
          style({background: param.bakgrunnsfarge, outline: `1px solid ${param.rammefarge}`, borderRadius: '4px', offset: 0.25}),
          style({background: param.initialBakgrunnsfarge, offset: 0.5}),
          style({background: param.bakgrunnsfarge, offset: 0.75}),
          style({background: param.initialBakgrunnsfarge, outline: 'none', offset: 1})
      ]));
}

/** blinker element når dette oppstår i dom.outline:'blue'
 * bruk: @Component({...., animations: [blinkInnAnimasjon()], ....})
 * og i html <element @blinkInn> */
export function blinkInnAnimasjon(innstillinger: Parameters<typeof animerBlink>[0] = {}) {
    return trigger('blinkInn', [
        transition(':enter', [animerBlink(innstillinger)])
    ]);
}

/** blinker element når dette oppstår i dom og angitt tallverdi øker.
 * bruk: @Component({...., animations: [betingetblinkInnAnimasjon()], ....})
 * og i html <element [@blinkInnBetinget]="teller"> der 'teller' er et tall som ved hver økning vil trige blinking */
export function betingetblinkInnAnimasjon(innstillinger: Parameters<typeof animerBlink>[0] & Partial<{ domElementSomAnimeres: string }> = {}) {
    const {domElementSomAnimeres} = {domElementSomAnimeres: ':self', ...innstillinger};
    return trigger('blinkInnBetinget', [
        transition(':increment', [
            query(domElementSomAnimeres, [
                animerBlink(innstillinger)
            ])
        ])
    ]);
}
